// ─────────────────────────────────────────────────────────────
// SessionsTab — admin-only view of Marie session captures.
//
// Layout:
//   • Filter row (date range, user, model, "errors only" toggle)
//   • Bulk-action row (sticky, only visible when >=1 row checked)
//   • Sessions table (Date / User / Title / Model / counts / duration / actions)
//   • Pagination
//   • Session viewer modal — clicking a row opens it
//
// The viewer (SessionViewerDialog) fetches GET /api/admin/marie-sessions/{id}
// and renders the full session in-app: a chronological transcript of chat
// turns, inline code snapshots, and run-result consoles. The single-session
// ZIP export is kept as a secondary action inside the viewer; the bulk
// export is unchanged.
// ─────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import UnfoldLessOutlinedIcon from "@mui/icons-material/UnfoldLessOutlined";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import {
  adminMarieSessionsApi,
  adminUsersApi,
  marieSessionsApi,
} from "../../api/client";
import type {
  AdminUser,
  MarieSessionHeader,
  MarieSessionListParams,
  MarieSessionDetail,
  MarieSessionTurn,
  MarieSessionCodeSnapshot,
  MarieSessionRun,
} from "../../api/client";

// Hard cap matching the backend (POST /api/admin/marie-sessions/export
// rejects requests with > 50 ids). Must stay in sync with Sam's contract.
const BULK_EXPORT_CAP = 50;

const PAGE_SIZE = 50;

// Admin filter dropdown. Mirrors AI_MODELS but kept independent because
// sessions captured BEFORE a catalog change can still carry retired model
// ids — the filter list is "what's worth filtering for going forward", not
// a strict mirror of the current picker. As of 2026-05-15 the live picker
// is Claude Opus 4.7/4.6 + GPT-5.4/5-mini/5; older sessions tagged with
// gpt-4.1* or gemini-* are still queryable by typing the raw id into the
// URL if needed, but those entries no longer clutter the dropdown.
const MODEL_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Any model" },
  { value: "claude-opus-4-7", label: "Claude Opus 4.7" },
  { value: "claude-opus-4-6", label: "Claude Opus 4.6" },
  { value: "gpt-5.4", label: "GPT-5.4" },
  { value: "gpt-5-mini", label: "GPT-5-mini" },
  { value: "gpt-5", label: "GPT-5" },
];

interface FilterState {
  userId: number | null;
  dateFrom: string; // YYYY-MM-DD or ""
  dateTo: string;
  hasErrors: boolean;
  model: string;
}

const EMPTY_FILTERS: FilterState = {
  userId: null,
  dateFrom: "",
  dateTo: "",
  hasErrors: false,
  model: "",
};

/**
 * Format a UTC ISO timestamp into a compact "MMM D, YYYY h:mm a" string
 * for the table. Falls back to the raw value on parse failure so admins
 * can at least see what they're filtering against.
 */
function formatStartedAt(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format a duration in milliseconds as "Nm Ms" / "Ms" / "—" for null.
 * Sessions that haven't ended report null duration; show an em dash.
 */
function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function formatUserCell(s: MarieSessionHeader, users?: AdminUser[]): string {
  if (s.user_full_name) return s.user_full_name;
  if (s.user_email) return s.user_email;
  if (s.user_id != null && users) {
    const u = users.find((x) => x.id === s.user_id);
    if (u) return u.full_name ?? u.email;
  }
  return s.user_id != null ? `User #${s.user_id}` : "Unknown";
}

function buildListParams(
  filters: FilterState,
  page: number,
): MarieSessionListParams {
  return {
    user_id: filters.userId,
    date_from: filters.dateFrom || null,
    date_to: filters.dateTo || null,
    has_errors: filters.hasErrors || null,
    model: filters.model || null,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };
}

export function SessionsTab() {
  // ── Filters / pagination state ───────────────────────────
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  // The filters that are currently *applied* — only changes on Apply/Clear.
  // Keeps the table stable while the admin tweaks inputs.
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);

  // ── Data state ───────────────────────────────────────────
  const [sessions, setSessions] = useState<MarieSessionHeader[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Users — populated once for the user filter Autocomplete.
  const [users, setUsers] = useState<AdminUser[]>([]);

  // ── Selection / bulk state ───────────────────────────────
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // ── Per-row download state ───────────────────────────────
  const [singleBusyId, setSingleBusyId] = useState<number | null>(null);

  // ── Session viewer state ─────────────────────────────────
  // Holds the id of the session whose full transcript is open in the
  // viewer dialog. The dialog fetches its own nested detail by id.
  const [viewSessionId, setViewSessionId] = useState<number | null>(null);

  // ── Delete dialog state ──────────────────────────────────
  // Bulk delete: open with the snapshot of currently-selected ids so
  // toggling checkboxes mid-confirmation can't change what's deleted.
  const [deleteSelectedOpen, setDeleteSelectedOpen] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);

  // ── Snackbar ─────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "info" });

  function showSnackbar(
    message: string,
    severity: "success" | "error" | "info" | "warning" = "info",
  ) {
    setSnackbar({ open: true, message, severity });
  }

  // Initial user list — populates the user filter dropdown. Soft-fail:
  // the dropdown gracefully falls back to "All users" if this errors.
  useEffect(() => {
    adminUsersApi
      .list()
      .then(setUsers)
      .catch(() => setUsers([]));
  }, []);

  // Fetch sessions when applied filters or page changes.
  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildListParams(appliedFilters, page);
      const res = await adminMarieSessionsApi.list(params);
      setSessions(res.sessions);
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load sessions");
      setSessions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page]);

  useEffect(() => {
    void fetchSessions();
  }, [fetchSessions]);

  // ── Filter handlers ──────────────────────────────────────
  function handleApply() {
    setPage(0);
    setSelected(new Set()); // selections are bound to the current result page
    setAppliedFilters(filters);
  }

  function handleClear() {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(0);
    setSelected(new Set());
  }

  // ── Selection helpers ────────────────────────────────────
  const allOnPageSelected =
    sessions.length > 0 && sessions.every((s) => selected.has(s.id));

  function handleToggleAll(_: unknown, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        sessions.forEach((s) => next.add(s.id));
      } else {
        sessions.forEach((s) => next.delete(s.id));
      }
      return next;
    });
  }

  function handleToggleOne(id: number, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function handleClearSelection() {
    setSelected(new Set());
  }

  // ── Downloads ────────────────────────────────────────────
  async function handleDownloadOne(sessionId: number) {
    if (singleBusyId != null) return;
    setSingleBusyId(sessionId);
    try {
      await marieSessionsApi.triggerSessionDownload(sessionId);
      showSnackbar("Session exported.", "success");
    } catch (e) {
      const detail = e instanceof Error ? e.message : "Unknown error";
      showSnackbar(`Couldn't export — ${detail}`, "error");
    } finally {
      setSingleBusyId(null);
    }
  }

  async function handleDownloadSelected() {
    if (bulkBusy) return;
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (ids.length > BULK_EXPORT_CAP) {
      showSnackbar(
        `Bulk export capped at ${BULK_EXPORT_CAP} sessions. Reduce your selection.`,
        "warning",
      );
      return;
    }
    setBulkBusy(true);
    try {
      await adminMarieSessionsApi.triggerBulkDownload(ids);
      showSnackbar(`Exported ${ids.length} session${ids.length === 1 ? "" : "s"}.`, "success");
    } catch (e) {
      const detail = e instanceof Error ? e.message : "Unknown error";
      showSnackbar(`Couldn't export — ${detail}`, "error");
    } finally {
      setBulkBusy(false);
    }
  }

  // ── Deletes ──────────────────────────────────────────────
  // Confirmation is the only friction — admin JWT alone is sufficient on
  // the backend (no separate admin password required).
  async function handleDeleteSelectedSubmit(ids: number[]): Promise<void> {
    const res = await adminMarieSessionsApi.deleteBulk(ids);
    showSnackbar(
      `Deleted ${res.deleted} session${res.deleted === 1 ? "" : "s"}.`,
      "warning",
    );
    setDeleteSelectedOpen(false);
    setSelected(new Set());
    void fetchSessions();
  }

  async function handleDeleteAllSubmit(): Promise<void> {
    const res = await adminMarieSessionsApi.deleteAll();
    showSnackbar(
      `Deleted ${res.deleted} session${res.deleted === 1 ? "" : "s"} — Marie session log cleared.`,
      "warning",
    );
    setDeleteAllOpen(false);
    setSelected(new Set());
    void fetchSessions();
  }

  // ── Pagination ───────────────────────────────────────────
  const startIdx = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIdx = Math.min((page + 1) * PAGE_SIZE, total);
  const totalPages = total === 0 ? 1 : Math.ceil(total / PAGE_SIZE);

  // ── Filter row UI helpers ────────────────────────────────
  function handleModelChange(e: SelectChangeEvent) {
    setFilters((f) => ({ ...f, model: e.target.value }));
  }
  function handleUserChange(e: SelectChangeEvent) {
    const v = e.target.value;
    setFilters((f) => ({
      ...f,
      userId: v === "" ? null : Number(v),
    }));
  }

  const selectedCount = selected.size;
  const overCap = selectedCount > BULK_EXPORT_CAP;

  return (
    <Box>
      {/* ── Filter row ──────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: "8px",
          bgcolor: "background.paper",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", md: "center" }}
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            label="From"
            type="date"
            size="small"
            value={filters.dateFrom}
            onChange={(e) =>
              setFilters((f) => ({ ...f, dateFrom: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={filters.dateTo}
            onChange={(e) =>
              setFilters((f) => ({ ...f, dateTo: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <Select
            size="small"
            displayEmpty
            value={filters.userId == null ? "" : String(filters.userId)}
            onChange={handleUserChange}
            sx={{ minWidth: 200 }}
            renderValue={(value) => {
              if (!value) return <Typography variant="caption" color="text.secondary">All users</Typography>;
              const u = users.find((x) => String(x.id) === value);
              return u ? (u.full_name ?? u.email) : `User #${value}`;
            }}
          >
            <MenuItem value="">All users</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={String(u.id)}>
                {u.full_name ?? u.email}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            displayEmpty
            value={filters.model}
            onChange={handleModelChange}
            sx={{ minWidth: 180 }}
            renderValue={(value) => {
              const opt = MODEL_OPTIONS.find((m) => m.value === value);
              return opt ? opt.label : "Any model";
            }}
          >
            {MODEL_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || "any"} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Switch
              size="small"
              checked={filters.hasErrors}
              onChange={(_, c) => setFilters((f) => ({ ...f, hasErrors: c }))}
              inputProps={{ "aria-label": "Errors only" }}
            />
            <Typography variant="caption" color="text.secondary">
              Errors only
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />

          <Button size="small" variant="outlined" onClick={handleClear}>
            Clear
          </Button>
          <Button size="small" variant="contained" onClick={handleApply}>
            Apply
          </Button>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchSessions} aria-label="Refresh sessions">
              <RefreshOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {/* Visual separator + extra padding before the destructive
              "Delete All" button so it can't be confused with Apply. */}
          <Box
            sx={{
              alignSelf: "stretch",
              borderLeft: "1px solid",
              borderColor: "divider",
              mx: 0.5,
            }}
          />
          <Tooltip
            title={
              total === 0
                ? "No sessions to delete."
                : "Permanently delete all Marie sessions across all users."
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={total === 0}
                onClick={() => setDeleteAllOpen(true)}
                startIcon={<DeleteForeverOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: "none" }}
              >
                Delete All Sessions
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {/* ── Bulk action row (sticky, conditional) ─────── */}
      {selectedCount > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            mb: 1,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "primary.light",
            color: "primary.contrastText",
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          <Chip
            label={`${selectedCount} selected${overCap ? " (over cap)" : ""}`}
            size="small"
            color={overCap ? "warning" : "default"}
            sx={{ fontWeight: 600 }}
          />
          <Tooltip
            title={
              overCap
                ? `Bulk export capped at ${BULK_EXPORT_CAP} sessions. Reduce your selection.`
                : "Download the selected sessions as a single zip"
            }
          >
            <span>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleDownloadSelected}
                disabled={overCap || bulkBusy}
                startIcon={
                  bulkBusy ? (
                    <CircularProgress size={12} thickness={5} />
                  ) : (
                    <DownloadOutlinedIcon sx={{ fontSize: 14 }} />
                  )
                }
                sx={{ textTransform: "none" }}
              >
                Download Selected
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              overCap
                ? "Bulk delete is capped at 50 sessions. Reduce your selection."
                : "Permanently delete the selected sessions"
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => setDeleteSelectedOpen(true)}
                disabled={overCap || selectedCount === 0}
                startIcon={<DeleteOutlineOutlinedIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  // The bulk row sits on a primary-tinted background so the
                  // default error palette renders as low-contrast pink. Pin
                  // the surface to the paper color so the destructive intent
                  // reads clearly.
                  bgcolor: "background.paper",
                  "&:hover": { bgcolor: "background.paper" },
                }}
              >
                Delete Selected
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="text"
            onClick={handleClearSelection}
            sx={{ textTransform: "none", color: "inherit" }}
          >
            Clear selection
          </Button>
        </Paper>
      )}

      {/* ── Table ──────────────────────────────────────── */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {!loading && error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && (
        <Box
          sx={{
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            overflow: "auto",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "background.default" }}>
                <TableCell sx={{ width: 36 }}>
                  <Checkbox
                    size="small"
                    checked={allOnPageSelected}
                    indeterminate={
                      !allOnPageSelected &&
                      sessions.some((s) => selected.has(s.id))
                    }
                    onChange={handleToggleAll}
                    inputProps={{ "aria-label": "Select all on this page" }}
                  />
                </TableCell>
                <SessionHeaderCell label="Started" width={160} />
                <SessionHeaderCell label="User" />
                <SessionHeaderCell label="Title" />
                <SessionHeaderCell label="Model" width={130} />
                <SessionHeaderCell
                  label="Turns / Runs / Errors"
                  width={130}
                  align="right"
                />
                <SessionHeaderCell label="Duration" width={90} align="right" />
                <SessionHeaderCell label="Actions" width={90} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.disabled">
                      No sessions match these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((s) => {
                  const isSelected = selected.has(s.id);
                  return (
                    <TableRow
                      key={s.id}
                      hover
                      selected={isSelected}
                      onClick={() => setViewSessionId(s.id)}
                      sx={{ cursor: "pointer" }}
                      aria-label={`View session ${s.id}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={(_, c) => handleToggleOne(s.id, c)}
                          inputProps={{
                            "aria-label": `Select session ${s.id}`,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatStartedAt(s.started_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {formatUserCell(s, users)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {s.title ?? <em style={{ opacity: 0.6 }}>(untitled)</em>}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={s.model ?? "—"}
                          size="small"
                          sx={{ height: 18, fontSize: "0.625rem" }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="caption"
                          color={s.error_count > 0 ? "error.main" : "text.secondary"}
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {s.turn_count} / {s.run_count} / {s.error_count}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {formatDuration(s.duration_ms)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                          <Tooltip title="Download session as zip">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadOne(s.id)}
                                disabled={singleBusyId === s.id}
                                aria-label="Download session"
                                sx={{ minWidth: 32, minHeight: 32 }}
                              >
                                {singleBusyId === s.id ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* ── Pagination ─────────────────────────────────── */}
      {!loading && !error && total > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1.5,
            px: 0.5,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing {startIdx}–{endIdx} of {total}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              sx={{ textTransform: "none" }}
            >
              Previous
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
              Page {page + 1} of {totalPages}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              sx={{ textTransform: "none" }}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Session viewer ──────────────────────────────── */}
      <SessionViewerDialog
        sessionId={viewSessionId}
        users={users}
        onClose={() => setViewSessionId(null)}
        onDownload={handleDownloadOne}
        downloadingId={singleBusyId}
      />

      {/* ── Delete dialogs ──────────────────────────────── */}
      <DeleteSelectedDialog
        open={deleteSelectedOpen}
        ids={Array.from(selected)}
        onClose={() => setDeleteSelectedOpen(false)}
        onSubmit={handleDeleteSelectedSubmit}
        onError={(msg) => showSnackbar(msg, "error")}
      />
      <DeleteAllDialog
        open={deleteAllOpen}
        total={total}
        onClose={() => setDeleteAllOpen(false)}
        onSubmit={handleDeleteAllSubmit}
        onError={(msg) => showSnackbar(msg, "error")}
      />

      {/* ── Snackbar ────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ fontSize: "0.8125rem" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ── Sub-components ────────────────────────────────────────────

interface SessionHeaderCellProps {
  label: string;
  width?: number;
  align?: "left" | "right";
}

function SessionHeaderCell({ label, width, align = "left" }: SessionHeaderCellProps) {
  return (
    <TableCell
      align={align}
      sx={{
        fontWeight: 600,
        fontSize: "0.6875rem",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "text.secondary",
        ...(width ? { width } : {}),
      }}
    >
      {label}
    </TableCell>
  );
}

// ── Session Viewer Dialog ─────────────────────────────────────
//
// Full in-app session viewer. Fetches GET /api/admin/marie-sessions/{id}
// on open and renders the complete session: a chronological transcript of
// chat turns interleaved with code snapshots and run-result consoles.
//
// Timeline ordering: turns carry `created_at`, snapshots `captured_at`,
// runs `created_at`. We merge all three onto one timeline sorted by their
// timestamps so a code injection / run shows up inline at the point it
// happened relative to the conversation. Turns without a timestamp fall
// back to their `sequence` so they never collapse out of order.

/** A single renderable item on the merged session timeline. */
type TimelineItem =
  | { kind: "turn"; sortKey: number; data: MarieSessionTurn }
  | { kind: "code"; sortKey: number; data: MarieSessionCodeSnapshot }
  | { kind: "run"; sortKey: number; data: MarieSessionRun };

/** Parse an ISO timestamp to epoch ms; NaN-safe (returns null on failure). */
function parseTs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Build the merged, chronologically-sorted timeline. Items with a real
 * timestamp sort by it; turns without one fall back to a synthetic key
 * derived from `sequence` so the conversation never reorders. Snapshots /
 * runs missing a timestamp sink to the end (they're rare — defensive only).
 */
function buildTimeline(detail: MarieSessionDetail): TimelineItem[] {
  const items: TimelineItem[] = [];

  for (const t of detail.turns ?? []) {
    const ts = parseTs(t.created_at);
    // Fall back to sequence-based ordering when no timestamp is present.
    items.push({
      kind: "turn",
      sortKey: ts ?? t.sequence,
      data: t,
    });
  }
  for (const c of detail.code_snapshots ?? []) {
    const ts = parseTs(c.captured_at);
    items.push({ kind: "code", sortKey: ts ?? Number.MAX_SAFE_INTEGER, data: c });
  }
  for (const r of detail.runs ?? []) {
    const ts = parseTs(r.created_at);
    items.push({ kind: "run", sortKey: ts ?? Number.MAX_SAFE_INTEGER, data: r });
  }

  return items.sort((a, b) => a.sortKey - b.sortKey);
}

/** True for a run that did not succeed (non-success status or non-zero exit). */
function isRunFailed(run: MarieSessionRun): boolean {
  if (run.status && run.status !== "success") return true;
  return run.exit_code != null && run.exit_code !== 0;
}

/** Human label for a code-snapshot `source` value. */
function snapshotSourceLabel(source: string): string {
  switch (source) {
    case "marie_inject":
      return "Marie injected code";
    case "user_edit":
      return "User edited code";
    case "run":
      return "Code at run time";
    case "load":
      return "Loaded code";
    default:
      return source || "Code snapshot";
  }
}

interface SessionViewerDialogProps {
  sessionId: number | null;
  users: AdminUser[];
  onClose: () => void;
  onDownload: (id: number) => void;
  downloadingId: number | null;
}

function SessionViewerDialog({
  sessionId,
  users,
  onClose,
  onDownload,
  downloadingId,
}: SessionViewerDialogProps) {
  const open = sessionId != null;

  const [detail, setDetail] = useState<MarieSessionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Which code snapshots are expanded. Greg reads the user↔Marie interaction
  // far more often than the code, so snapshots start COLLAPSED — this set is
  // the allow-list of snapshot ids the admin has chosen to open. Reset on
  // every new session so a re-open never carries another session's state.
  const [expandedCode, setExpandedCode] = useState<Set<number>>(new Set());

  // Which run-output blocks are expanded. Same rationale as expandedCode:
  // Greg scans the chat far more than the verbose stdout/stderr, so runs
  // start COLLAPSED — the collapsed header still surfaces status / exit code
  // / duration so failures are obvious without opening. Reset per session.
  const [expandedRuns, setExpandedRuns] = useState<Set<number>>(new Set());

  const toggleCode = useCallback((id: number) => {
    setExpandedCode((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleRun = useCallback((id: number) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Fetch the nested detail whenever the dialog opens for a new session.
  useEffect(() => {
    setExpandedCode(new Set());
    setExpandedRuns(new Set());
    if (sessionId == null) {
      // Clear stale content so a re-open shows the loader, not the prior
      // session's transcript flashing through.
      setDetail(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    adminMarieSessionsApi
      .detail(sessionId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Unknown error";
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const timeline = useMemo(
    () => (detail ? buildTimeline(detail) : []),
    [detail],
  );

  // Ids of every code snapshot and run in this session — drive the header's
  // single global expand/collapse-all control, which covers BOTH verbose
  // block types so one click opens/closes everything that's hidden.
  const codeSnapshotIds = useMemo(
    () => (detail?.code_snapshots ?? []).map((c) => c.id),
    [detail],
  );
  const runIds = useMemo(
    () => (detail?.runs ?? []).map((r) => r.id),
    [detail],
  );
  const hasVerboseBlocks = codeSnapshotIds.length > 0 || runIds.length > 0;
  const allVerboseExpanded =
    hasVerboseBlocks &&
    codeSnapshotIds.every((id) => expandedCode.has(id)) &&
    runIds.every((id) => expandedRuns.has(id));

  // One toggle drives both sets: if everything is already open, collapse
  // all; otherwise open every code snapshot and every run.
  const handleExpandAll = useCallback(() => {
    const everythingOpen =
      hasVerboseBlocks &&
      codeSnapshotIds.every((id) => expandedCode.has(id)) &&
      runIds.every((id) => expandedRuns.has(id));
    if (everythingOpen) {
      setExpandedCode(new Set());
      setExpandedRuns(new Set());
    } else {
      setExpandedCode(new Set(codeSnapshotIds));
      setExpandedRuns(new Set(runIds));
    }
  }, [codeSnapshotIds, runIds, expandedCode, expandedRuns, hasVerboseBlocks]);

  const downloading = sessionId != null && downloadingId === sessionId;

  // Resolve a display name for the session owner from the users list.
  const userLabel = useMemo(() => {
    if (!detail) return "";
    if (detail.user_id != null) {
      const u = users.find((x) => x.id === detail.user_id);
      if (u) return u.full_name ?? u.email;
      return `User #${detail.user_id}`;
    }
    return "Unknown";
  }, [detail, users]);

  // Derived duration from started/ended (the nested payload omits it).
  const durationMs = useMemo(() => {
    if (!detail) return null;
    const start = parseTs(detail.started_at);
    const end = parseTs(detail.ended_at);
    if (start == null || end == null) return null;
    return end - start;
  }, [detail]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // A session transcript can be tall; let the dialog use most of the
      // viewport and scroll its own body rather than the page.
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "block", lineHeight: 1.2 }}
        >
          Marie Session{sessionId != null ? ` #${sessionId}` : ""}
        </Typography>
        <Typography variant="subtitle1" sx={{ lineHeight: 1.3 }}>
          {detail?.title ?? "(untitled session)"}
        </Typography>
        <IconButton
          aria-label="Close session viewer"
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 12, right: 12 }}
        >
          <CloseOutlinedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 0, bgcolor: "background.default", display: "flex", flexDirection: "column" }}
      >
        {/* Loading */}
        {loading && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {/* Error (404 / network) */}
        {!loading && error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" variant="outlined">
              {error.toLowerCase().includes("not found")
                ? "This session could not be found. It may have been deleted."
                : `Couldn't load this session — ${error}`}
            </Alert>
          </Box>
        )}

        {/* Loaded content */}
        {!loading && !error && detail && (
          <>
            {/* Metadata summary strip */}
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                bgcolor: "background.paper",
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              <SummaryChip label="User" value={userLabel} />
              <SummaryChip label="Started" value={formatStartedAt(detail.started_at)} />
              <SummaryChip
                label="Ended"
                value={detail.ended_at ? formatStartedAt(detail.ended_at) : "in progress"}
              />
              <SummaryChip label="Duration" value={formatDuration(durationMs)} />
              <SummaryChip label="Model" value={detail.model ?? "—"} />
              <SummaryChip label="Turns" value={String(detail.turn_count)} />
              <SummaryChip label="Runs" value={String(detail.run_count)} />
              <SummaryChip
                label="Errors"
                value={String(detail.error_count)}
                emphasize={detail.error_count > 0}
              />
              {hasVerboseBlocks && (
                <>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleExpandAll}
                    startIcon={
                      allVerboseExpanded ? (
                        <UnfoldLessOutlinedIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <UnfoldMoreOutlinedIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    sx={{ textTransform: "none", fontSize: "0.6875rem" }}
                  >
                    {allVerboseExpanded ? "Collapse all" : "Expand all"}
                  </Button>
                </>
              )}
            </Box>

            {/* Timeline */}
            <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
              {timeline.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.disabled"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  This session has no recorded turns, code, or runs.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {timeline.map((item) => {
                    if (item.kind === "turn") {
                      return <TurnBubble key={`t-${item.data.id}`} turn={item.data} />;
                    }
                    if (item.kind === "code") {
                      return (
                        <CodeSnapshotBlock
                          key={`c-${item.data.id}`}
                          snapshot={item.data}
                          expanded={expandedCode.has(item.data.id)}
                          onToggle={() => toggleCode(item.data.id)}
                        />
                      );
                    }
                    return (
                      <RunResultBlock
                        key={`r-${item.data.id}`}
                        run={item.data}
                        expanded={expandedRuns.has(item.data.id)}
                        onToggle={() => toggleRun(item.data.id)}
                      />
                    );
                  })}
                </Stack>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="outlined"
          startIcon={
            downloading ? (
              <CircularProgress size={12} thickness={5} />
            ) : (
              <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={() => sessionId != null && onDownload(sessionId)}
          disabled={downloading || sessionId == null}
        >
          Download as ZIP
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Viewer sub-components ──────────────────────────────────────

/** A compact "Label: value" pill for the metadata summary strip. */
function SummaryChip({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: "6px",
        border: "1px solid",
        borderColor: emphasize ? "error.main" : "divider",
        bgcolor: "background.default",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontSize: "0.625rem",
          color: "text.secondary",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontSize: "0.75rem",
          color: emphasize ? "error.main" : "text.primary",
          fontWeight: emphasize ? 600 : 400,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/** One chat turn — user vs. Marie distinguished by side, color, and icon. */
function TurnBubble({ turn }: { turn: MarieSessionTurn }) {
  const isUser = turn.role === "user";
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
      }}
    >
      <Box
        sx={{
          maxWidth: "82%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: isUser ? "flex-end" : "flex-start",
        }}
      >
        {/* Role label */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mb: 0.25,
            px: 0.5,
          }}
        >
          {isUser ? (
            <PersonOutlineOutlinedIcon
              sx={{ fontSize: 13, color: "text.secondary" }}
            />
          ) : (
            <AutoAwesomeOutlinedIcon
              sx={{ fontSize: 13, color: "primary.main" }}
            />
          )}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: "0.6875rem",
              color: isUser ? "text.secondary" : "primary.main",
            }}
          >
            {isUser ? "User" : "Marie"}
          </Typography>
          {turn.created_at && (
            <Typography
              variant="caption"
              sx={{ fontSize: "0.625rem", color: "text.disabled" }}
            >
              {formatStartedAt(turn.created_at)}
            </Typography>
          )}
          {turn.image_attached && (
            <Chip
              label="image attached"
              size="small"
              sx={{ height: 16, fontSize: "0.5625rem" }}
            />
          )}
        </Box>
        {/* Bubble */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: "10px",
            bgcolor: isUser ? "primary.main" : "background.paper",
            color: isUser ? "primary.contrastText" : "text.primary",
            border: isUser ? "none" : "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8125rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {turn.content}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/** Shared monospace block styling for code + console output. */
const monoBlockSx = {
  m: 0,
  p: 1.25,
  borderRadius: "8px",
  fontFamily:
    '"Roboto Mono", "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
  fontSize: "0.75rem",
  lineHeight: 1.55,
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
  overflowX: "auto" as const,
};

/** Count the lines in a code snapshot for the collapsed summary row. */
function countLines(code: string | null | undefined): number {
  if (!code) return 0;
  // Trailing newline shouldn't add a phantom blank line to the count.
  const trimmed = code.replace(/\n+$/, "");
  if (trimmed === "") return 0;
  return trimmed.split("\n").length;
}

/**
 * An inline code snapshot in a monospace card. Collapsed by default so the
 * chat interaction stays the prominent content; clicking the header row (a
 * disclosure with a rotating chevron) expands the code in place. The
 * collapsed state still shows source/label + line count so it's obvious
 * there's code here and what it is.
 */
function CodeSnapshotBlock({
  snapshot,
  expanded,
  onToggle,
}: {
  snapshot: MarieSessionCodeSnapshot;
  expanded: boolean;
  onToggle: () => void;
}) {
  const label = snapshot.label || snapshotSourceLabel(snapshot.source);
  const lines = countLines(snapshot.code);

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: "8px", overflow: "hidden", bgcolor: "background.paper" }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${expanded ? "Collapse" : "Expand"} code snapshot — ${label}`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          cursor: "pointer",
          bgcolor: "background.default",
          borderBottom: expanded ? "1px solid" : "none",
          borderColor: "divider",
          userSelect: "none",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <ChevronRightOutlinedIcon
          sx={{
            fontSize: 18,
            color: "text.secondary",
            transition: "transform 150ms ease",
            transform: expanded ? "rotate(90deg)" : "none",
          }}
        />
        <CodeOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, fontSize: "0.6875rem", color: "text.secondary" }}
        >
          {label}
        </Typography>
        {!expanded && (
          <Typography
            variant="caption"
            sx={{ fontSize: "0.625rem", color: "text.disabled" }}
          >
            · {lines} line{lines === 1 ? "" : "s"}
          </Typography>
        )}
        {snapshot.captured_at && (
          <Typography
            variant="caption"
            sx={{ fontSize: "0.625rem", color: "text.disabled" }}
          >
            {formatStartedAt(snapshot.captured_at)}
          </Typography>
        )}
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box
          component="pre"
          sx={{ ...monoBlockSx, bgcolor: "background.paper", color: "text.primary" }}
        >
          {snapshot.code}
        </Box>
      </Collapse>
    </Paper>
  );
}

/**
 * A run result — status header + stdout/stderr consoles. Collapsed by
 * default (mirrors CodeSnapshotBlock) so the chat interaction stays the
 * prominent content; clicking the header (a disclosure with a rotating
 * chevron) reveals the verbose stdout/stderr in place. The collapsed header
 * still surfaces the at-a-glance run info — status, exit code, duration —
 * so Greg can scan pass/fail without opening anything, and failures stay
 * obvious via the error-colored chip + left border.
 */
function RunResultBlock({
  run,
  expanded,
  onToggle,
}: {
  run: MarieSessionRun;
  expanded: boolean;
  onToggle: () => void;
}) {
  const failed = isRunFailed(run);
  const hasStdout = run.stdout != null && run.stdout.trim() !== "";
  const hasStderr = run.stderr != null && run.stderr.trim() !== "";

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: "background.paper",
        // Failed runs get an error-colored left border for at-a-glance scan.
        borderLeft: "4px solid",
        borderLeftColor: failed ? "error.main" : "success.main",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${expanded ? "Collapse" : "Expand"} run output — ${
          run.status || (failed ? "failure" : "success")
        }`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          px: 1.25,
          py: 0.75,
          cursor: "pointer",
          userSelect: "none",
          bgcolor: "background.default",
          borderBottom: expanded ? "1px solid" : "none",
          borderColor: "divider",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <ChevronRightOutlinedIcon
          sx={{
            fontSize: 18,
            color: "text.secondary",
            transition: "transform 150ms ease",
            transform: expanded ? "rotate(90deg)" : "none",
          }}
        />
        <TerminalOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, fontSize: "0.6875rem", color: "text.secondary" }}
        >
          Run output
        </Typography>
        <Chip
          icon={
            failed ? (
              <ErrorOutlineOutlinedIcon sx={{ fontSize: 13 }} />
            ) : (
              <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 13 }} />
            )
          }
          label={run.status || (failed ? "failure" : "success")}
          size="small"
          color={failed ? "error" : "success"}
          variant={failed ? "filled" : "outlined"}
          sx={{ height: 18, fontSize: "0.625rem", textTransform: "capitalize" }}
        />
        {run.exit_code != null && (
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.625rem",
              color: failed ? "error.main" : "text.disabled",
              fontWeight: failed ? 600 : 400,
            }}
          >
            exit {run.exit_code}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{ fontSize: "0.625rem", color: "text.disabled" }}
        >
          {formatDuration(run.duration_ms)}
        </Typography>
        {run.created_at && (
          <Typography
            variant="caption"
            sx={{ fontSize: "0.625rem", color: "text.disabled" }}
          >
            {formatStartedAt(run.created_at)}
          </Typography>
        )}
      </Box>

      {/* Consoles — hidden until the header disclosure is expanded. */}
      <Collapse in={expanded} unmountOnExit>
      <Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
        {!hasStdout && !hasStderr && (
          <Typography
            variant="caption"
            sx={{ color: "text.disabled", fontStyle: "italic" }}
          >
            (no console output)
          </Typography>
        )}
        {hasStdout && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: "0.625rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "text.secondary",
              }}
            >
              stdout
            </Typography>
            <Box
              component="pre"
              sx={{
                ...monoBlockSx,
                mt: 0.25,
                bgcolor: "background.default",
                color: "text.primary",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {run.stdout}
            </Box>
          </Box>
        )}
        {hasStderr && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: "0.625rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "error.main",
              }}
            >
              stderr
            </Typography>
            <Box
              component="pre"
              sx={{
                ...monoBlockSx,
                mt: 0.25,
                bgcolor: "background.default",
                color: "error.main",
                border: "1px solid",
                borderColor: "error.main",
              }}
            >
              {run.stderr}
            </Box>
          </Box>
        )}
      </Box>
      </Collapse>
    </Paper>
  );
}

// ── Delete Selected Dialog ────────────────────────────────────
//
// Plain "are you sure" confirmation. The admin JWT is the only auth gate
// (handled by the backend); the dialog itself is just a click-to-confirm
// affordance so the admin can't fat-finger Delete by accident.

interface DeleteSelectedDialogProps {
  open: boolean;
  ids: number[];
  onClose: () => void;
  onSubmit: (ids: number[]) => Promise<void>;
  onError: (message: string) => void;
}

function DeleteSelectedDialog({
  open,
  ids,
  onClose,
  onSubmit,
  onError,
}: DeleteSelectedDialogProps) {
  const [busy, setBusy] = useState(false);

  // Snapshot the ids when the dialog opens so the count stays stable even
  // if the parent's selection changes mid-confirmation.
  const [snapshotIds, setSnapshotIds] = useState<number[]>([]);
  useEffect(() => {
    if (open) {
      setSnapshotIds(ids);
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const count = snapshotIds.length;

  async function handleSubmit() {
    if (busy) return;
    setBusy(true);
    try {
      await onSubmit(snapshotIds);
      // Parent closes on success.
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      onError(`Couldn't delete — ${msg}`);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        Delete {count} session{count === 1 ? "" : "s"}?
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          This will permanently delete {count} Marie session
          {count === 1 ? "" : "s"}, including their chat turns, code
          snapshots, and run output artifacts. The runs themselves remain
          in the execution log but will no longer be linked. This action
          cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void handleSubmit()}
          disabled={busy}
          autoFocus
          startIcon={
            busy ? <CircularProgress size={14} color="inherit" /> : undefined
          }
        >
          {busy ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete All Dialog ─────────────────────────────────────────
//
// Plain "are you sure" confirmation. The admin JWT is the only auth gate;
// the dialog body warns that this affects all users so the click feels
// deliberate.

interface DeleteAllDialogProps {
  open: boolean;
  total: number;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  onError: (message: string) => void;
}

function DeleteAllDialog({
  open,
  total,
  onClose,
  onSubmit,
  onError,
}: DeleteAllDialogProps) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setBusy(false);
  }, [open]);

  async function handleSubmit() {
    if (busy) return;
    setBusy(true);
    try {
      await onSubmit();
      // Parent closes on success.
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      onError(`Couldn't delete — ${msg}`);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1, color: "error.main" }}>
        Delete ALL Marie sessions?
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Alert severity="warning" variant="outlined">
          This will permanently delete all <strong>{total}</strong> Marie
          session{total === 1 ? "" : "s"} across all users, including chat
          turns, code snapshots, and run artifacts. This cannot be undone.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void handleSubmit()}
          disabled={busy}
          startIcon={
            busy ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <DeleteForeverOutlinedIcon sx={{ fontSize: 16 }} />
            )
          }
        >
          {busy ? "Deleting…" : "Delete All"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
